import React, { useRef, useState, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import { X, Printer, CheckCircle, AlertCircle } from 'lucide-react';
import BusinessUnitName from './BusinessUnitName';
import { formatDateTime } from './utils/dateUtils';
import { useTimezone } from '../../../../hooks/useTimezone';
import TruckNotesThread from './TruckNotesThread';

const InboundRecordViewModal = ({ isOpen, onClose, record }) => {
  const printRef = useRef();
  const [userNames, setUserNames] = useState({});
  const userTimezone = useTimezone();

  // Fetch user names for created_by and updated_by fields
  useEffect(() => {
    const fetchUserNames = async () => {
      if (!record) return;

      const userIdsToFetch = [];

      if (record.created_by) {
        userIdsToFetch.push(record.created_by);
      }

      if (record.updated_by && record.updated_by !== record.created_by) {
        userIdsToFetch.push(record.updated_by);
      }

      if (userIdsToFetch.length === 0) return;

      try {
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const token = localStorage.getItem('token');

        const userPromises = userIdsToFetch.map(async (userId) => {
          try {
            const response = await fetch(`${API_URL}/users/${userId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.ok) {
              const user = await response.json();
              return {
                id: userId,
                name: user.full_name || user.username || `User ${userId}`
              };
            }
          } catch (error) {
            console.warn(`Failed to fetch user ${userId}:`, error);
          }

          return {
            id: userId,
            name: `User ${userId}` // Fallback
          };
        });

        const userResults = await Promise.all(userPromises);

        const newUserNames = {};
        userResults.forEach(result => {
          if (result) {
            newUserNames[result.id] = result.name;
          }
        });

        setUserNames(newUserNames);

      } catch (error) {
        console.error('Error fetching user names:', error);
      }
    };

    if (isOpen && record) {
      fetchUserNames();
    }
  }, [isOpen, record]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Inbound_Record_${record?.id || 'export'}_${new Date().toISOString().split('T')[0]}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  if (!isOpen || !record) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-300 bg-gray-50">
          <h2 className="text-2xl font-semibold text-gray-800">Inbound Delivery Record</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div ref={printRef} className="p-8 bg-white print-content">
            <style jsx>{`
              @media print {
                .print-content {
                  font-family: Arial, sans-serif;
                  color: #000;
                  font-size: 12px;
                  line-height: 1.4;
                }
                .print-header {
                  border-bottom: 2px solid #000;
                  margin-bottom: 20px;
                  padding-bottom: 15px;
                }
                .print-title {
                  font-size: 24px;
                  font-weight: bold;
                  margin: 0 0 10px 0;
                }
                .print-subtitle {
                  font-size: 12px;
                  color: #666;
                  margin: 2px 0;
                }
                .print-section {
                  margin-bottom: 20px;
                  page-break-inside: avoid;
                }
                .print-section-title {
                  font-size: 16px;
                  font-weight: bold;
                  margin: 0 0 10px 0;
                  padding-bottom: 5px;
                  border-bottom: 1px solid #ccc;
                }
                .print-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 10px;
                }
                .print-table th,
                .print-table td {
                  border: 1px solid #ccc;
                  padding: 8px;
                  text-align: left;
                  font-size: 11px;
                }
                .print-table th {
                  background-color: #f5f5f5;
                  font-weight: bold;
                }
                .print-table td:first-child {
                  font-weight: bold;
                  width: 30%;
                  background-color: #f9f9f9;
                }
                .print-notes-box {
                  background: #f5f5f5;
                  border: 1px solid #ccc;
                  padding: 10px;
                  white-space: pre-wrap;
                  word-wrap: break-word;
                  font-size: 11px;
                }
                .print-notes-warning {
                  background: #fee;
                  border-color: #fcc;
                }
                .print-badge {
                  display: inline-block;
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 11px;
                  font-weight: bold;
                }
                .print-badge-success {
                  background: #d4edda;
                  color: #155724;
                  border: 1px solid #c3e6cb;
                }
                .print-badge-warning {
                  background: #fff3cd;
                  color: #856404;
                  border: 1px solid #ffeeba;
                }
                .print-footer {
                  margin-top: 30px;
                  padding-top: 15px;
                  border-top: 1px solid #ccc;
                  font-size: 10px;
                  color: #666;
                  text-align: center;
                }
              }
              @media screen {
                .print-content {
                  font-family: Arial, sans-serif;
                  color: #000;
                  font-size: 12px;
                  line-height: 1.4;
                }
                .print-header {
                  border-bottom: 2px solid #000;
                  margin-bottom: 20px;
                  padding-bottom: 15px;
                }
                .print-title {
                  font-size: 24px;
                  font-weight: bold;
                  margin: 0 0 10px 0;
                }
                .print-subtitle {
                  font-size: 12px;
                  color: #666;
                  margin: 2px 0;
                }
                .print-section {
                  margin-bottom: 20px;
                }
                .print-section-title {
                  font-size: 16px;
                  font-weight: bold;
                  margin: 0 0 10px 0;
                  padding-bottom: 5px;
                  border-bottom: 1px solid #ccc;
                }
                .print-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 10px;
                }
                .print-table th,
                .print-table td {
                  border: 1px solid #ccc;
                  padding: 8px;
                  text-align: left;
                  font-size: 11px;
                }
                .print-table th {
                  background-color: #f5f5f5;
                  font-weight: bold;
                }
                .print-table td:first-child {
                  font-weight: bold;
                  width: 30%;
                  background-color: #f9f9f9;
                }
                .print-notes-box {
                  background: #f5f5f5;
                  border: 1px solid #ccc;
                  padding: 10px;
                  white-space: pre-wrap;
                  word-wrap: break-word;
                  font-size: 11px;
                }
                .print-notes-warning {
                  background: #fee;
                  border-color: #fcc;
                }
                .print-badge {
                  display: inline-block;
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 11px;
                  font-weight: bold;
                }
                .print-badge-success {
                  background: #d4edda;
                  color: #155724;
                  border: 1px solid #c3e6cb;
                }
                .print-badge-warning {
                  background: #fff3cd;
                  color: #856404;
                  border: 1px solid #ffeeba;
                }
                .print-footer {
                  margin-top: 30px;
                  padding-top: 15px;
                  border-top: 1px solid #ccc;
                  font-size: 10px;
                  color: #666;
                  text-align: center;
                }
              }
            `}</style>

            {/* HEADER SECTION */}
            <div className="print-header">
              <h1 className="print-title">Inbound Delivery Record</h1>
              <p className="print-subtitle">Record ID: {record.id}</p>
              <p className="print-subtitle">Confirmation #: {record.conf_number || 'N/A'}</p>
              <p className="print-subtitle">Generated: {new Date().toLocaleString()}</p>
            </div>

            {/* STATUS SECTION */}
            <div className="print-section">
              <h2 className="print-section-title">Status Information</h2>
              <table className="print-table">
                <tbody>
                  <tr>
                    <td>Status</td>
                    <td>
                      <span className="print-badge print-badge-success">{record.status}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Conforming</td>
                    <td>
                      <span className={`print-badge ${record.conforming ? 'print-badge-success' : 'print-badge-warning'}`}>
                        {record.conforming ? 'Yes - Conforming' : 'No - Non-Conforming (See Notes)'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* NOTES SECTION (multi-note thread) */}
            <div className="print-section">
              <h2 className="print-section-title">Notes</h2>
              <TruckNotesThread
                truckType="inbound"
                truckId={record.id}
                mode="read"
                emptyMessage="No notes on this record."
              />
            </div>

            {/* SHIPMENT DETAILS SECTION */}
            <div className="print-section">
              <h2 className="print-section-title">Delivery Details</h2>
              <table className="print-table">
                <tbody>
                  <tr>
                    <td>Carrier</td>
                    <td>{record.carrier}</td>
                  </tr>
                  <tr>
                    <td>Business Unit</td>
                    <td><BusinessUnitName id={record.business_unit} /></td>
                  </tr>
                  <tr>
                    <td>Vendor</td>
                    <td>{record.vendor || '—'}</td>
                  </tr>
                  <tr>
                    <td>Load Type</td>
                    <td>{record.load_type}</td>
                  </tr>
                  <tr>
                    <td>Load Material</td>
                    <td>{record.load_material || '—'}</td>
                  </tr>
                  <tr>
                    <td>Size</td>
                    <td>{record.size} {record.size === 1 ? 'pallet' : 'pallets'}</td>
                  </tr>
                  <tr>
                    <td>PO Number</td>
                    <td>{record.po || '—'}</td>
                  </tr>
                  <tr>
                    <td>Order Number</td>
                    <td>{record.order_number || '—'}</td>
                  </tr>
                  <tr>
                    <td>Priority</td>
                    <td>{record.priority || 'Normal'}</td>
                  </tr>
                  <tr>
                    <td>Secure Handling</td>
                    <td>{record.secure ? 'Yes - Required' : 'No'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* SCHEDULING INFORMATION SECTION */}
            <div className="print-section">
              <h2 className="print-section-title">Scheduling Information</h2>
              <table className="print-table">
                <tbody>
                  <tr>
                    <td>Scheduled Date/Time</td>
                    <td>{record.scheduled_at ? formatDateTime(record.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone) : '—'}</td>
                  </tr>
                  <tr>
                    <td>Dock Door</td>
                    <td>{record.dock_door || '—'}</td>
                  </tr>
                  <tr>
                    <td>Staging Area</td>
                    <td>{record.staging_area || '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* TIMELINE SECTION */}
            <div className="print-section">
              <h2 className="print-section-title">Delivery Timeline</h2>
              <table className="print-table">
                <thead>
                  <tr>
                    <th style={{width: '25%'}}>Stage</th>
                    <th style={{width: '40%'}}>Timestamp</th>
                    <th style={{width: '35%'}}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Scheduled</td>
                    <td>{record.scheduled_at ? formatDateTime(record.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone) : '—'}</td>
                    <td>✓ Completed</td>
                  </tr>
                  {record.arrived_at && (
                    <tr>
                      <td>Arrived</td>
                      <td>{formatDateTime(record.arrived_at, 'MMM dd, yyyy h:mm a', userTimezone)}</td>
                      <td>✓ Completed</td>
                    </tr>
                  )}
                  {record.received_at && (
                    <tr>
                      <td>Received</td>
                      <td>{formatDateTime(record.received_at, 'MMM dd, yyyy h:mm a', userTimezone)}</td>
                      <td>✓ Completed</td>
                    </tr>
                  )}
                  {record.putaway_at && (
                    <tr>
                      <td>Putaway</td>
                      <td>{formatDateTime(record.putaway_at, 'MMM dd, yyyy h:mm a', userTimezone)}</td>
                      <td>✓ Completed</td>
                    </tr>
                  )}
                  {record.completed_at && (
                    <tr>
                      <td>Completed</td>
                      <td>{formatDateTime(record.completed_at, 'MMM dd, yyyy h:mm a', userTimezone)}</td>
                      <td>✓ Completed</td>
                    </tr>
                  )}
                  {record.cancelled_at && (
                    <tr>
                      <td>Cancelled</td>
                      <td>{formatDateTime(record.cancelled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</td>
                      <td style={{color: '#dc2626'}}>✗ Cancelled</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* RECORD INFORMATION SECTION */}
            <div className="print-section">
              <h2 className="print-section-title">Record Information</h2>
              <table className="print-table">
                <tbody>
                  <tr>
                    <td>Created By</td>
                    <td>{record.created_by ? (userNames[record.created_by] || 'Loading...') : 'System'}</td>
                  </tr>
                  <tr>
                    <td>Created At</td>
                    <td>{record.created_at ? formatDateTime(record.created_at, 'MMM dd, yyyy h:mm a', userTimezone) : '—'}</td>
                  </tr>
                  <tr>
                    <td>Last Updated By</td>
                    <td>{record.updated_by ? (userNames[record.updated_by] || 'Loading...') : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td>Last Updated At</td>
                    <td>{record.updated_at ? formatDateTime(record.updated_at, 'MMM dd, yyyy h:mm a', userTimezone) : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* FOOTER */}
            <div className="print-footer">
              <p>
                This document was generated from the KSP 3PL Headquarters Truck Scheduler System on {new Date().toLocaleString()}.
              </p>
              <p>
                For the most up-to-date information, please refer to the live system.
              </p>
            </div>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="flex gap-3 p-6 border-t border-gray-300 bg-gray-50">
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print to PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default InboundRecordViewModal;
